/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { FunctionComponent } from 'react'

import { EuiCallOut, EuiLink, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useLocalStorage } from '@modules/utils/hooks/useLocalStorage'
import LocalStorageKey from '@modules/utils/localStorageKeys'
import type { AnyProject } from '@modules/project-api/types'
import { editDefaultSpacesUrl } from '@modules/project-lib/kibanaLinks'

const ProjectCpsSpacesConfigurationCallout: FunctionComponent<{ project: AnyProject }> = ({
  project,
}) => {
  const [isCpsSpacesConfigurationCalloutDismissed, setIsCpsSpacesConfigurationCalloutDismissed] =
    useLocalStorage(LocalStorageKey.cpsSpacesConfigurationCalloutDismissed, false)

  if (isCpsSpacesConfigurationCalloutDismissed) {
    return null
  }

  return (
    <EuiCallOut
      title={
        <FormattedMessage
          id='projectCps.spacesConfigurationCallout.title'
          defaultMessage='Manage search context in Kibana'
        />
      }
      color='primary'
      iconType='info'
      onDismiss={() => setIsCpsSpacesConfigurationCalloutDismissed(true)}
    >
      <EuiText size='s' color='subdued'>
        <p>
          <FormattedMessage
            id='projectCps.spacesConfigurationCallout.description'
            defaultMessage="All queries in this project will automatically include data from all linked projects by default. To customize the default search scope for specific Kibana spaces, adjust the project's search settings."
          />
        </p>
        <p>
          <EuiLink
            href={editDefaultSpacesUrl(project)}
            target='_blank'
            data-test-subj='cps-spacesConfigurationCallout-learnMoreLink'
          >
            <FormattedMessage
              id='projectCps.spacesConfigurationCallout.learnMore'
              defaultMessage='Configure space settings in Kibana'
            />
          </EuiLink>
        </p>
      </EuiText>
    </EuiCallOut>
  )
}

export default ProjectCpsSpacesConfigurationCallout
